// Admin → Feedback (admins only, v4 D7): what users sent through the Director: problems (report_problem), ideas
// (send_feedback) and the asks it couldn't do yet (unmet). Filter by kind, mark seen / done. Each one was also emailed
// to support. GET /api/admin/feedback?kind=&status=, PATCH /api/admin/feedback/:id {status}.
import { useState } from "react";
import { authedBlobUrl, call } from "../api";
import { Empty, ErrorNote, Spinner, timeAgo, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { Link } from "../router";
import { FILTERS, KIND_LABEL, normFeedback, type FeedbackItem, type FeedbackKind, type FeedbackStatus } from "../feedback";
import { toast, useStore } from "../store";

// the mock app (VITE_MOCK=1) reads in-memory rows (mock/feedback.ts); the import is dropped from the real build
const MOCK = import.meta.env.VITE_MOCK === "1";
const listFeedback = (kind: "" | FeedbackKind) =>
  MOCK ? import("../mock/feedback").then((m) => m.mockFeedback(kind)) : call("GET", `admin/feedback${kind ? `?kind=${kind}` : ""}`);
const markFeedback = (id: string, status: FeedbackStatus) =>
  MOCK ? import("../mock/feedback").then((m) => m.mockMarkFeedback(id, status)) : call("PATCH", `admin/feedback/${encodeURIComponent(id)}`, { status });

export function AdminFeedbackScreen() {
  const me = useStore((s) => s.me);
  const admin = me?.user.role === "admin";
  const [kind, setKind] = useState<"" | FeedbackKind>("");
  const { data, setData, error, loading, reload } = useLoad(
    () => (admin ? listFeedback(kind).then(normFeedback) : Promise.resolve([] as FeedbackItem[])),
    [admin, kind],
  );

  if (!admin)
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-primary">Back home</Link>}>
            Feedback is read by the studio's admins.
          </Empty>
        </div>
      </Shell>
    );

  const mark = async (it: FeedbackItem, status: FeedbackStatus) => {
    try {
      await markFeedback(it.id, status);
      setData((data ?? []).map((x) => (x.id === it.id ? { ...x, status } : x)));
    } catch (e) {
      toast(`Couldn't update it. ${(e as Error).message}`, "bad");
    }
  };

  const open = (data ?? []).filter((x) => x.status !== "done").length;
  return (
    <Shell>
      <div className="page">
        <header className="page-head">
          <h1 className="page-title">Feedback</h1>
          <p className="page-lede">What people told the Director: problems, ideas, and things they asked for that it can't do yet. Each one was also emailed to support.</p>
        </header>
        <div className="fb-filters" role="tablist" aria-label="Kind">
          {FILTERS.map((f) => (
            <button key={f.id || "all"} type="button" role="tab" aria-selected={kind === f.id} className={`chip${kind === f.id ? " is-on" : ""}`} onClick={() => setKind(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        {error && <ErrorNote text={`Couldn't load the feedback. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Loading" />}
        {data && !data.length && <p className="muted">Nothing here yet.</p>}
        {!!data?.length && (
          <>
            <p className="muted">{open} open</p>
            <ul className="fb-rows">
              {data.map((it) => (
                <li key={it.id} className={`fb-row${it.status === "done" ? " is-done" : ""}`}>
                  <div className="fb-head">
                    <span className={`fb-kind fb-kind-${it.kind}`}>{KIND_LABEL[it.kind]}</span>
                    {it.source === "auto" && <span className="fb-kind">Logged by the app</span>}
                    <span>{it.user.name ?? it.user.email ?? "Someone"}</span>
                    {it.createdAt && <span>{timeAgo(it.createdAt)}</span>}
                    {it.status === "new" && <span className="status status-busy">New</span>}
                  </div>
                  <p className="fb-text">{it.text}</p>
                  {it.context.screenshot && <FeedbackShot id={it.id} />}
                  {(it.projectId || it.context.path || it.context.jobIds?.length || it.context.sentryEventIds?.length) && (
                    <p className="fb-meta">
                      {/* the parts joined by " · " with none left hanging at the end (X3) */}
                      {[
                        it.projectId && (
                          <span key="v">
                            Video: <Link to={`/p/${it.projectId}`}>{it.projectTitle ?? it.projectId}</Link>
                          </span>
                        ),
                        it.context.path && `Screen ${it.context.path}`,
                        it.context.jobIds?.length && `Jobs ${it.context.jobIds.slice(0, 3).join(", ")}`,
                        it.context.sentryEventIds?.length && `Errors ${it.context.sentryEventIds.join(", ")}`,
                      ]
                        .filter(Boolean)
                        .map((part, i) => (
                          <span key={i}>
                            {i > 0 && " · "}
                            {part}
                          </span>
                        ))}
                    </p>
                  )}
                  <div className="fb-acts">
                    {it.status === "new" && (
                      <button type="button" className="btn btn-quiet btn-small" onClick={() => void mark(it, "seen")}>
                        Mark seen
                      </button>
                    )}
                    {it.status !== "done" ? (
                      <button type="button" className="btn btn-quiet btn-small" onClick={() => void mark(it, "done")}>
                        Done
                      </button>
                    ) : (
                      <button type="button" className="btn btn-quiet btn-small" onClick={() => void mark(it, "seen")}>
                        Reopen
                      </button>
                    )}
                    {it.user.email && (
                      <a className="btn btn-quiet btn-small" href={`mailto:${it.user.email}`}>
                        Email them
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Shell>
  );
}

// 2026-11-02: the picture of the screen sent with the report button (loaded on tap: it's behind admin sign-in)
function FeedbackShot({ id }: { id: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "failed">("idle");
  if (url)
    return (
      <a className="fb-shot" href={url} target="_blank" rel="noreferrer">
        <img src={url} alt="The user's screen when they sent this" />
      </a>
    );
  return (
    <button
      type="button"
      className="btn btn-quiet btn-small"
      disabled={state === "loading"}
      onClick={async () => {
        setState("loading");
        try {
          setUrl(await authedBlobUrl(`admin/feedback/${encodeURIComponent(id)}/screenshot`));
        } catch {
          setState("failed");
        }
      }}
    >
      {state === "loading" ? "Loading the screenshot" : state === "failed" ? "Couldn't load the screenshot. Try again" : "Show the screenshot"}
    </button>
  );
}
